/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hooks, and context co-located */
/**
 * InlineEditContext — conversation-level single-edit-at-a-time coordination.
 *
 * Tracks which inline content item is currently being edited. When a new item
 * requests edit mode while another has unsaved changes, shows a warning modal
 * with Discard / Continue Editing options.
 *
 * Uses refs internally to avoid re-rendering all consumers when activeEditId
 * changes — consumers subscribe via callback pattern (like AutoSaveCoordinationProvider).
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { InlineConfirmationModal } from '@/lib/base/overlays/InlineConfirmationModal';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Handlers an item registers so the context can interact with it when switching edits. */
interface ItemHandlers {
  /** Returns true if the item currently has unsaved or in-progress changes. */
  hasPendingChanges: () => boolean;
  /** Discard all unsaved changes. */
  discard: () => void;
  /** Scroll the item's element into view. */
  scrollIntoView: () => void;
}

export interface InlineEditContextValue {
  /**
   * Request to start editing an item. Returns true if the edit can proceed.
   * Returns false if the current item had unsaved changes and the user chose
   * "Keep Editing" (blocking the switch).
   */
  requestEdit: (id: string) => Promise<boolean>;
  /** Release the active edit lock for the given ID. No-op if not active. */
  releaseEdit: (id: string) => void;
  /** Discard unsaved changes for the given ID and release the edit lock. */
  discardAndReleaseEdit: (id: string) => void;
  /**
   * Register handlers for unsaved-changes detection when switching edits.
   * Returns an unregister cleanup function.
   */
  registerItem: (id: string, handlers: ItemHandlers) => () => void;
  /**
   * Subscribe to active edit ID changes. Callback fires immediately with the
   * current value. Returns an unsubscribe function.
   */
  subscribeToActiveEdit: (
    callback: (activeId: string | null) => void
  ) => () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const InlineEditContext = createContext<InlineEditContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

interface InlineEditProviderProps {
  children: ReactNode;
}

/**
 * Provider that coordinates single-edit-at-a-time state across inline content
 * items in a scrollable conversation container.
 *
 * @example
 * ```tsx
 * <InlineEditProvider>
 *   <ConversationScrollContainer>
 *     <InlineEditableContentWrapper editId="item-1" skeletonHeight={120}>
 *       <MyEditableContent />
 *     </InlineEditableContentWrapper>
 *   </ConversationScrollContainer>
 * </InlineEditProvider>
 * ```
 */
export function InlineEditProvider({ children }: InlineEditProviderProps) {
  // Active edit and subscribers tracked in refs — no tree-wide re-renders
  const activeEditIdRef = useRef<string | null>(null);
  const itemHandlersRef = useRef<Map<string, ItemHandlers>>(new Map());
  const subscribersRef = useRef<Set<(id: string | null) => void>>(new Set());

  // Modal state for "switch edit with unsaved changes" warning
  const [pendingEditId, setPendingEditId] = useState<string | null>(null);
  const pendingResolveRef = useRef<((value: boolean) => void) | null>(null);

  const notifySubscribers = useCallback((id: string | null) => {
    subscribersRef.current.forEach(cb => {
      try {
        cb(id);
      } catch {
        // Ignore subscriber errors to prevent one bad subscriber from blocking others
      }
    });
  }, []);

  const registerItem = useCallback((id: string, handlers: ItemHandlers) => {
    itemHandlersRef.current.set(id, handlers);
    return () => {
      itemHandlersRef.current.delete(id);
    };
  }, []);

  const subscribeToActiveEdit = useCallback(
    (callback: (id: string | null) => void) => {
      subscribersRef.current.add(callback);
      // Immediately fire with current value so subscriber doesn't miss state
      callback(activeEditIdRef.current);
      return () => {
        subscribersRef.current.delete(callback);
      };
    },
    []
  );

  const releaseEdit = useCallback(
    (id: string) => {
      if (activeEditIdRef.current === id) {
        activeEditIdRef.current = null;
        notifySubscribers(null);
      }
    },
    [notifySubscribers]
  );

  const discardAndReleaseEdit = useCallback(
    (id: string) => {
      if (activeEditIdRef.current === id) {
        itemHandlersRef.current.get(id)?.discard();
        activeEditIdRef.current = null;
        notifySubscribers(null);
      }
    },
    [notifySubscribers]
  );

  const requestEdit = useCallback(
    (id: string): Promise<boolean> => {
      const currentId = activeEditIdRef.current;

      // Already the active edit — no-op
      if (currentId === id) {
        return Promise.resolve(true);
      }

      const currentHandlers = currentId
        ? itemHandlersRef.current.get(currentId)
        : null;
      const hasPending = currentHandlers?.hasPendingChanges() ?? false;

      if (!hasPending) {
        activeEditIdRef.current = id;
        notifySubscribers(id);
        return Promise.resolve(true);
      }

      // Has unsaved changes — show warning modal and defer resolution.
      // If a modal is already open (previous request in flight), reject
      // the new request so the earlier caller's promise isn't orphaned.
      if (pendingResolveRef.current) {
        return Promise.resolve(false);
      }

      return new Promise<boolean>(resolve => {
        pendingResolveRef.current = resolve;
        setPendingEditId(id);
      });
    },
    [notifySubscribers]
  );

  const handleModalDiscard = useCallback(() => {
    const currentId = activeEditIdRef.current;
    if (currentId) {
      itemHandlersRef.current.get(currentId)?.discard();
    }

    const newId = pendingEditId;
    setPendingEditId(null);
    activeEditIdRef.current = newId;
    notifySubscribers(newId);
    pendingResolveRef.current?.(true);
    pendingResolveRef.current = null;
  }, [pendingEditId, notifySubscribers]);

  const handleModalKeepEditing = useCallback(() => {
    const currentId = activeEditIdRef.current;
    if (currentId) {
      itemHandlersRef.current.get(currentId)?.scrollIntoView();
    }

    setPendingEditId(null);
    pendingResolveRef.current?.(false);
    pendingResolveRef.current = null;
  }, []);

  const value = useMemo(
    () => ({
      requestEdit,
      releaseEdit,
      discardAndReleaseEdit,
      registerItem,
      subscribeToActiveEdit,
    }),
    [
      requestEdit,
      releaseEdit,
      discardAndReleaseEdit,
      registerItem,
      subscribeToActiveEdit,
    ]
  );

  return (
    <InlineEditContext.Provider value={value}>
      {children}
      {pendingEditId !== null && (
        <InlineConfirmationModal
          isOpen
          title='Unsaved changes'
          description='You have unsaved changes. Discard them or continue editing.'
          onDiscard={handleModalDiscard}
          onCancel={handleModalKeepEditing}
        />
      )}
    </InlineEditContext.Provider>
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Access inline edit coordination context.
 * Throws if no InlineEditProvider is found.
 */
export function useInlineEditContext(): InlineEditContextValue {
  const context = useContext(InlineEditContext);
  if (!context) {
    throw new Error(
      'useInlineEditContext must be used within InlineEditProvider'
    );
  }
  return context;
}

/**
 * Optionally access inline edit coordination context.
 * Returns null if no InlineEditProvider is found (graceful fallback).
 */
export function useInlineEditContextOptional(): InlineEditContextValue | null {
  return useContext(InlineEditContext);
}
